import { Trans } from '@lingui/react/macro'
import { useNavigate } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import CantileverButton from 'components/CantileverButton'
import { ProcessFlowDiagram } from 'features/processFlowDiagrams'
import { useRolodexEntries } from 'features/rolodex'
import React, { useCallback, useMemo, useState } from 'react'
import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

import { SlashCommandCardProps } from './DefaultSlashCommandCard'
import {
  ClickableSlashCommandCardName,
  SlashCommandCard,
  SlashCommandCardBody,
  SlashCommandCardHeader,
  SlashCommandCardIcon,
  SlashCommandCardInfo,
  SlashCommandCardMeta,
  SlashCommandCardType,
  SlashCommandChip,
  SlashCommandChipIcon,
  SlashCommandModal,
  SlashCommandModalBody,
  SlashCommandModalContent,
  SlashCommandModalHeader,
} from './sharedStyles'

const PFDContainer = styled.div`
  height: 30rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.25rem;
  margin: 1rem;
  display: flex;
  flex-direction: column;
`

export const SystemSlashCommandCard: React.FC<SlashCommandCardProps> = ({
  entryType,
  entryID,
  entryName,
  isEditMode,
  onClick,
}) => {
  const [showModal, setShowModal] = useState(false)
  const navigate = useNavigate()
  const { BASE_PATH } = useRuntimeVariables()

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const { systems } = useRolodexEntries({
    ids: [entryID],
    type: RolodexEntryType.SYSTEM,
  })

  const system = useMemo(() => systems.at(0), [systems])

  const pfdId = system?.tags?.[RolodexEntryType.PROCESS_FLOW_DIAGRAM]
  const siteId = system?.tags?.site

  const handleClick = useCallback(
    (event?: React.MouseEvent) => {
      if (isEditMode) {
        setShowModal(true)
        onClick?.()
      } else if (system) {
        const siteId = system.tags.site

        if (!siteId) {
          console.warn('No site ID found for system:', entryID)
          return
        }

        if (event?.metaKey || event?.ctrlKey) {
          const baseUrl = BASE_PATH ? `/${BASE_PATH}` : ''
          const search = new URLSearchParams({
            [SEARCH_PARAM_SITE_ID]: siteId,
          })
          const url = `${baseUrl}/system-lens?${search.toString()}`
          window.open(url, '_blank')
          return
        }

        navigate({
          to: '/system-lens',
          search: {
            [SEARCH_PARAM_SITE_ID]: siteId,
          },
        })
      }
    },
    [system, entryID, isEditMode, onClick, navigate, BASE_PATH]
  )

  // TODO: Add configuration to show system lens customization
  if (isEditMode) {
    return (
      <>
        <SlashCommandChip
          onClick={handleClick}
          title={`${entryType}: ${entryName}`}>
          <SlashCommandChipIcon>S</SlashCommandChipIcon>
          {entryName}
        </SlashCommandChip>
        {showModal && (
          <SlashCommandModal onClick={handleCloseModal}>
            <SlashCommandModalContent onClick={(e) => e.stopPropagation()}>
              <SlashCommandModalHeader>
                <Trans>Configure metadata for System</Trans>
              </SlashCommandModalHeader>
              <SlashCommandModalBody>
                <p>
                  <strong>
                    <Trans>Entry:</Trans>
                  </strong>{' '}
                  {entryName}
                </p>
                <p>
                  <strong>
                    <Trans>ID:</Trans>
                  </strong>{' '}
                  {entryID}
                </p>
                <p>
                  <Trans>Coming soon: System View Configuration</Trans>
                </p>
              </SlashCommandModalBody>
              <CantileverButton primary onClick={handleCloseModal}>
                <Trans>Close</Trans>
              </CantileverButton>
            </SlashCommandModalContent>
          </SlashCommandModal>
        )}
      </>
    )
  }

  const systemName = system?.data.name
  const systemType = system?.data.type

  return (
    <SlashCommandCard $isEditMode={false} $disableHovering>
      <SlashCommandCardHeader>
        <SlashCommandCardIcon>S</SlashCommandCardIcon>
        <SlashCommandCardInfo>
          <ClickableSlashCommandCardName onClick={handleClick}>
            {entryName}
          </ClickableSlashCommandCardName>
          <SlashCommandCardType>
            <Trans>System</Trans>
          </SlashCommandCardType>
        </SlashCommandCardInfo>
      </SlashCommandCardHeader>
      <SlashCommandCardBody $isEditMode={false}>
        <SlashCommandCardMeta>
          {systemName && (
            <span>
              <Trans>Name: {systemName}</Trans>
            </span>
          )}
          {systemType && (
            <span>
              <Trans>Type: {systemType}</Trans>
            </span>
          )}
        </SlashCommandCardMeta>
        {pfdId && siteId && (
          <PFDContainer>
            <ProcessFlowDiagram
              siteID={siteId}
              diagramID={pfdId}
              viewOnly
              hideHeader
              hideMenuBar
            />
          </PFDContainer>
        )}
      </SlashCommandCardBody>
    </SlashCommandCard>
  )
}
